import { API } from "../../api.js";
import { drawCandlestickChart } from "../../ui/charts.js";
import { $, esc, fmtMoney } from "../../ui/format.js";
import { helpIcon } from "../../ui/help.js";

// ---------------------------------------------------------------- Public Markets

async function renderPublicMarkets(main, snap) {
  const company = window.__company;
  if (!company.is_public) {
    main.innerHTML = `
      <h2 class="panel-title">Public Markets — Month ${snap.month} ${helpIcon("publicmarkets")}</h2>
      <div class="plain-english-card">
        Not a public company yet. Going public (an IPO) lists your shares on the stock market: you raise money from the public,
        and from then on your share price, market cap and analysts show here every month. Underwriters need a $20,000,000
        valuation and $3,000,000 of yearly revenue first.
      </div>
      <div id="pm-ipo"></div>
    `;
    // the IPO card is drawn by views/investors.js (late-bound: core modules don't import later ones)
    if (window.renderIpoInto) await window.renderIpoInto($("pm-ipo")).catch((e) => { $("pm-ipo").innerHTML = `<p class="ma-note">${esc(e.message)}</p>`; });
    return;
  }

  main.innerHTML = `
    <h2 class="panel-title">Public Markets — Month ${snap.month} ${helpIcon("publicmarkets")}</h2>
    <div id="pm-takeover" class="em-inline"></div>
    <div id="public-markets-content">Loading…</div>
  `;
  // hostile-bid defence (views/takeover.js, late-bound: core modules don't import later ones)
  if (window.renderTakeoverInto) window.renderTakeoverInto($("pm-takeover")).catch(() => { $("pm-takeover").innerHTML = ""; });
  const content = $("public-markets-content");
  let data;
  try {
    const res = await fetch(`${API}/public-company`);
    data = await res.json();
    if (!res.ok || data.listed === false) {
      content.innerHTML = `<p style="color:var(--oxblood);">${esc(data.detail || "No trading data yet.")}</p>`;
      return;
    }
  } catch (e) {
    content.innerHTML = `<p style="color:var(--oxblood);">Could not reach the server.</p>`;
    return;
  }

  const priceCls = data.price_change_pct > 0 ? "pos" : data.price_change_pct < 0 ? "neg" : "";
  const ratingCls = data.analyst_rating === "Buy" ? "pos" : data.analyst_rating === "Sell" ? "neg" : "";
  content.innerHTML = `
    <div class="risk-stat-row" style="margin-bottom:20px;">
      <div class="risk-stat-box">
        <div class="risk-stat-label">Share Price</div>
        <div class="risk-stat-value">$${data.share_price.toFixed(2)}</div>
      </div>
      <div class="risk-stat-box">
        <div class="risk-stat-label">Since IPO (M${data.ipo_month})</div>
        <div class="risk-stat-value ${priceCls}">${data.price_change_pct > 0 ? "+" : ""}${data.price_change_pct}%</div>
      </div>
      <div class="risk-stat-box">
        <div class="risk-stat-label">Market Cap</div>
        <div class="risk-stat-value">${fmtMoney(data.market_cap)}</div>
      </div>
      <div class="risk-stat-box">
        <div class="risk-stat-label">Analyst Rating</div>
        <div class="risk-stat-value ${ratingCls}">${esc(data.analyst_rating)}</div>
      </div>
    </div>
    <div class="chart-wrap" style="height:auto; padding:16px 16px 10px;">
      <div style="display:flex; justify-content:space-between; align-items:baseline; margin-bottom:8px;">
        <span style="font-family:var(--font-mono); font-size:11px; letter-spacing:0.08em; color:var(--text-low); text-transform:uppercase;">Share Price · Monthly Candles</span>
        <span class="mono ${priceCls}" style="font-size:12px;">${data.price_change_pct > 0 ? "▲" : data.price_change_pct < 0 ? "▼" : "—"} ${Math.abs(data.price_change_pct)}%
          ${snap.month > 1 ? `<button type="button" class="mc-why" data-price-why>Why?</button>` : ""}</span>
      </div>
      <div class="mc-why-panel" id="price-why" hidden></div>
      <div id="candlestick-chart" class="ck-host"></div>
    </div>
    ${data.quarterly ? `
      <h3 style="font-family:var(--font-display); font-size:16px; margin:24px 0 10px;">Latest Quarter</h3>
      <table class="ledger">
        <tr><td class="label">Revenue vs. Prior Quarter</td><td class="mono">${fmtMoney(data.quarterly.last_quarter_revenue)} vs ${fmtMoney(data.quarterly.prior_quarter_revenue)}</td></tr>
        <tr><td class="label">QoQ Growth</td><td class="mono ${data.quarterly.qoq_revenue_growth_pct > 0 ? "pos" : "neg"}">${data.quarterly.qoq_revenue_growth_pct}%</td></tr>
        <tr><td class="label">Verdict</td><td class="mono ${data.quarterly.verdict === "Beat" ? "pos" : data.quarterly.verdict === "Miss" ? "neg" : ""}">${esc(data.quarterly.verdict)}</td></tr>
      </table>
    ` : `<p style="font-size:12px; color:var(--text-low); margin-top:14px;">Quarterly comparison needs at least 6 months of trading history.</p>`}
  `;

  // the share price follows the valuation (and investor mood): the same breakdown as month close's "Why?"
  const pw = main.querySelector("[data-price-why]");
  if (pw) pw.addEventListener("click", async () => {
    const box = main.querySelector("#price-why");
    box.hidden = !box.hidden;
    if (box.hidden || box.dataset.loaded) return;
    box.innerHTML = `<p class="mc-why-sum">Working out what moved it…</p>`;
    try {
      const ch = await fetch(`${API}/valuation/change?from_month=${snap.month - 1}&to_month=${snap.month}`).then((x) => x.json());
      box.innerHTML = `<p class="mc-why-sum">Your share price follows what the company is worth. What moved the valuation this month:</p>`
        + (window.gesValuationWhyHtml ? window.gesValuationWhyHtml(ch) : esc(ch.summary || ""));
      box.dataset.loaded = "1";
    } catch (e) { box.innerHTML = `<p class="mc-why-sum">Couldn't load the breakdown. Try again.</p>`; }
  });
  drawCandlestickChart(document.getElementById("candlestick-chart"), data.price_history); // the kit redraws on resize itself
}

export { renderPublicMarkets };
